<script lang="ts">
  import CodeIcon from '$icons/CodeIcon.svelte';
  import BlockQuoteIcon from '$icons/formatting/BlockQuoteIcon.svelte';
  import BoldIcon from '$icons/formatting/BoldIcon.svelte';
  import ItalicIcon from '$icons/formatting/ItalicIcon.svelte';
  import UnderlineIcon from '$icons/formatting/UnderlineIcon.svelte';
  import Button from '../Button.svelte';
  import DotGrid from '../DotGrid.svelte';
  import { onMount, onDestroy } from 'svelte';
  import { Editor, type Content } from '@tiptap/core';
  import StarterKit from '@tiptap/starter-kit';
  import Underline from '@tiptap/extension-underline';
  import Image from '@tiptap/extension-image';
  import Link from '@tiptap/extension-link';
  import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
  import { common, createLowlight } from 'lowlight';
  import ErrorWarningAlertIcon from '$icons/ErrorWarningAlertIcon.svelte';
  import RichEditorImage from './RichEditorImage.svelte';
  import { ContentType } from '$shared/lib';
  import 'highlight.js/styles/atom-one-dark.min.css';
  import { browser } from '$app/environment';
  import type { TiptapContent } from '$shared/util';
  export let value: string | Content;
  export let label = '';
  export let name = '';
  export let id = '';
  export let description = '';
  export let optional = false;
  export let errors: string[] = [];
  export let contentType: ContentType;

  let element: HTMLElement;
  export let editor: Editor;
  const lowlight = createLowlight(common);
  $: lowlight?.register(common);
  export let imageAllowed = false;
  onMount(() => {
    editor = new Editor({
      element,
      content: value,
      extensions: [
        StarterKit.configure({
          codeBlock: false,
          blockquote: {
            HTMLAttributes: {
              class: 'border-l-4 border-gray-dark-6 bg-gray-dark-1 ml-10 py-2 pl-4 pr-2 rounded-sm',
            },
          },
        }),
        // Typography,
        Underline.configure({
          HTMLAttributes: { class: 'text-underline' },
        }),
        CodeBlockLowlight.configure({
          lowlight,
          HTMLAttributes: {
            class: 'bg-black text-mint-dark-12 m-4 rounded p-2',
          },
        }),
        Image.configure({
          HTMLAttributes: {
            class: 'w-full object-cover object-center',
          },
        }),
        Link.configure({
          HTMLAttributes: {
            class: 'relative z-10 text-mint-dark-9',
            rel: 'noopener noreferrer nofollow',
            target: '_blank',
          },
        }),
      ],
      onTransaction: () => {
        // biome-ignore lint/correctness/noSelfAssign: force re-render so `editor.isActive` works as expected
        editor = editor;
      },
      onCreate: () => {
        value = JSON.stringify(editor?.getJSON());
      },
      onUpdate: () => {
        value = JSON.stringify(editor?.getJSON());
      },
      editorProps: {
        attributes: {
          class:
            'border-none text-gray-dark-11 h-fit min-h-[100px] m-2 rounded border outline-none p-1 space-y-1',
        },
      },
    });

    // Re-opening the post modal without refresh brings back the previous
    // contents, so we have to make sure it's formatted correctly
    if (value && typeof value === 'string' && value.startsWith('{"type":"doc"')) {
      editor?.commands.setContent(JSON.parse(value));
    } else if (value && typeof value === 'string') {
      // This all exists to support editing posts and comments that were created
      // before the TipTap integration. Without this, their formatting gets stripped,
      // and any whitespace / links get removed, and the logic breaks anyway because
      // it's expecting JSON. So this basically just converts that plain text into
      // a TipTap document with the appropriate formatting.
      // Once they get edited, they're in the TipTap JSON format from that point on.
      let convertedValue = { type: 'doc', content: [] } as TiptapContent;
      // biome-ignore lint/complexity/noForEach: TODO: Replace with for...of
      value.split('\n').forEach((line) => {
        if (line.trim() === '') {
          convertedValue.content.push({ type: 'paragraph' });
        } else {
          let urlRegex =
            /(\b(https?:\/\/|www\.)[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|\/]*|\b[-A-Z0-9+&@#\/%=~_|]*\.[A-Z0-9+&@#\/%=~_|]*\.(me|info|com|org|net|xyz|io|co)|\b[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}([-A-Z0-9+&@#\/%=~_|\/]*))/gi;
          let urlMatch = line.match(urlRegex);
          let contentArray = [];
          if (urlMatch) {
            let lastIndex = 0;
            // biome-ignore lint/complexity/noForEach: TODO: Replace with for...of
            urlMatch.forEach((url) => {
              let startIndex = line.indexOf(url, lastIndex);
              let endIndex = startIndex + url.length;
              // Push text before URL
              if (startIndex > lastIndex) {
                contentArray.push({
                  type: 'text',
                  text: line.substring(lastIndex, startIndex),
                });
              }
              // Push URL
              contentArray.push({
                type: 'text',
                marks: [
                  {
                    type: 'link',
                    attrs: {
                      href: url,
                      target: '_blank',
                      rel: 'noopener noreferrer nofollow',
                      class: 'relative z-10 text-mint-dark-9',
                    },
                  },
                ],
                text: url,
              });
              lastIndex = endIndex;
            });
            // Push text after last URL
            if (lastIndex < line.length) {
              contentArray.push({
                type: 'text',
                text: line.substring(lastIndex),
              });
            }
            // Add the constructed content array to a paragraph in the document
            convertedValue.content.push({
              type: 'paragraph',
              content: contentArray,
            });
          } else {
            // No URLs, push entire line as a paragraph
            convertedValue.content.push({
              type: 'paragraph',
              content: [{ type: 'text', text: line }],
            });
          }
        }
      });
      editor?.commands.setContent(convertedValue);
    }
  });

  onDestroy(() => {
    if (editor) {
      editor.destroy();
    }
  });
  const embolden = () => {
    editor.chain().focus().toggleBold().run();
  };
  const italicize = () => {
    editor.chain().focus().toggleItalic().run();
  };
  const underline = () => {
    editor.chain().focus().toggleUnderline().run();
  };
  const blockQuote = () => {
    editor.chain().focus().toggleBlockquote().run();
  };
  const codeBlock = () => {
    editor.chain().focus().toggleCodeBlock().run();
  };
  $: isImageActive = editor?.isActive('image');
</script>

{#if browser}
  <div>
    <label for={id} class="">
      {#if label}
        <p class="font-architekt text-gray-dark-12 mb-1 text-sm font-normal leading-5">
          {label}
          {#if optional}
            <span class="text-gray-dark-10 text-[12px]">(Optional)</span>
          {/if}
        </p>
      {/if}
      {#if description}
        <p class="text-gray-dark-11 mb-2 mt-1 text-xs">
          {description}
        </p>
      {/if}
    </label>
    <div class="border-gray-dark-4 relative rounded border">
      <div
        class="border-gray-dark-4 bg-gray-dark-2 sticky top-0 z-10 flex items-center gap-x-2 border-b p-1"
      >
        <DotGrid />
        <!-- Bold -->
        <Button
          secondary={editor?.isActive('bold')}
          tertiary={!editor?.isActive('bold')}
          icon
          class="relative"
          on:click={embolden}><BoldIcon class="h-5 w-5" /></Button
        >
        <!-- Italics -->
        <Button
          secondary={editor?.isActive('italic')}
          tertiary={!editor?.isActive('italic')}
          icon
          class="relative"
          on:click={italicize}><ItalicIcon class="h-5 w-5" /></Button
        >
        <!-- Underline -->
        <Button
          secondary={editor?.isActive('underline')}
          tertiary={!editor?.isActive('underline')}
          icon
          class="relative"
          on:click={underline}><UnderlineIcon class="h-5 w-5" /></Button
        >
        <!-- Block Quote -->
        <Button
          secondary={editor?.isActive('blockquote')}
          tertiary={!editor?.isActive('blockquote')}
          icon
          class="relative"
          on:click={blockQuote}><BlockQuoteIcon class="h-5 w-5" /></Button
        >
        <!-- Code block -->
        <Button
          secondary={editor?.isActive('codeBlock')}
          tertiary={!editor?.isActive('codeBlock')}
          icon
          class="relative"
          on:click={codeBlock}><CodeIcon class="h-5 w-5" /></Button
        >
        <!-- Image -->
        {#if imageAllowed}
          <RichEditorImage
            bind:isImageActive
            bind:contentType
            on:forum-image-posted={(e) => editor?.commands.setImage({ src: e.detail.src })}
          />
        {/if}
      </div>
      <div bind:this={element} class="max-h-[300px] overflow-y-scroll" />
    </div>
    {#if errors?.length > 0}
      {#each errors as error}
        {#if error}
          <div class="text-red-dark-10 mt-2 flex items-center justify-start gap-x-1 gap-y-0.5">
            <ErrorWarningAlertIcon />
            <p class="text-red-dark-10">{error}</p>
          </div>
        {/if}
      {/each}
    {/if}
    <input type="text" {name} bind:value class="sr-only" />
  </div>
{/if}
